import { queryOptions, useQuery } from '@tanstack/react-query';

import { STALE_TIMES } from '#lib/queryConstants';

import { fetchJobProgress, fetchJobsByProspectQueryId, getQueueNameForJobType } from '../../api/jobsApi';

import { jobKeys } from './keys';

export const runningJobsForProspectQueryOptions = (prospectQueryId) =>
	queryOptions({
		queryKey: jobKeys.byProspectQuery(prospectQueryId),
		queryFn: async () => {
			const { data, error } = await fetchJobsByProspectQueryId(prospectQueryId);
			if (error) throw error;
			return data ?? [];
		},
		staleTime: STALE_TIMES.REALTIME,
		refetchInterval: 2000, // Auto-refetch every 2 seconds
	});

export function useJobProgress(prospectQueryId) {
	return useQuery({
		...runningJobsForProspectQueryOptions(prospectQueryId),
		enabled: Boolean(prospectQueryId),
	});
}

export const jobProgressOptions = (queueName, jobId) =>
	queryOptions({
		queryKey: jobKeys.progress(queueName, jobId),
		queryFn: async () => {
			return await fetchJobProgress(queueName, jobId);
		},
		staleTime: STALE_TIMES.REALTIME,
		// Smart polling: stop on terminal states or on non-network errors.
		// The backend answers from its durable job table even when the Bull job is gone,
		// so a terminal state is stable — failure is data in the body, not an HTTP error.
		refetchInterval: (query) => {
			const error = query.state.error;

			// Continue polling on network errors (backend might be restarting)
			const isNetworkError = error?.message?.includes('Failed to fetch') || error?.message?.includes('Network');
			if (error && !isNetworkError) {
				return false;
			}

			const data = query.state.data;
			const status = data?.state;
			if (status === 'completed' || status === 'failed') {
				return false;
			}
			// Aborted jobs keep polling until the processor confirms the cancel (finishedAt set)
			if (status === 'aborted' && data?.finishedAt) {
				return false;
			}

			// Otherwise, poll every two seconds
			return 2000;
		},
		retry: false,
	});

export function useIndividualJobProgress(job) {
	const queueName = job ? getQueueNameForJobType(job.jobType) : null;
	const jobId = job?.bullJobId;
	const jobUuid = job?.id;
	return useQuery({
		...jobProgressOptions(queueName, jobId, jobUuid),
		enabled: Boolean(job && queueName && jobId),
	});
}
